import { useState } from 'react';
import { Power, RotateCcw } from 'lucide-react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { toast } from '@/shared/ui/sonner';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import { horodatageTexte } from '@/shared/utils/liveness';

/**
 * « Couper la production » et « Relancer la production » d'une centrale PV.
 *
 * Nomenclature validée par Boubacar le 26/09, après la vidéo de David (« ajouter couper
 * injection et couper production »). Le raisonnement complet est dans
 * `backend/app/services/production_pv.py` ; ce qui compte à l'écran :
 *
 *   - COUPER L'INJECTION N'EXISTE PAS chez Hoymiles (plancher 2 %). Le geste le plus bas
 *     est « Réduire l'injection au minimum (2 %) », juste à côté, dans le catalogue.
 *   - COUPER LA PRODUCTION arrête chaque micro-onduleur de la centrale, un par un, sur
 *     les numéros de série lus chez S-Miles au moment du clic.
 *   - GRISÉ quand S-Miles ne déclare aucun onduleur : le serveur mémorise le dernier
 *     inventaire lu (`onduleurs_declares`). Un nombre inconnu laisse le bouton actif ;
 *     le premier clic lit l'inventaire et grise le bouton s'il est vide.
 *   - « ENVOYÉ », JAMAIS « FAIT » : l'exécution réelle est relue chez Hoymiles. Les deux
 *     premiers ordres sont suivis de près (quelques secondes), les suivants par la veille
 *     PV, au plus tard cinq minutes après : voir `SUIVIS_EN_PARALLELE_MAX`.
 */
const GESTES = {
  couper: {
    label: 'Couper la production', icon: Power, tone: 'danger',
    warning: "Chaque micro-onduleur de la centrale sera arrêté : l'installation du client "
      + "ne produira plus rien, ni pour lui ni pour le réseau, jusqu'à la relance.",
  },
  relancer: {
    label: 'Relancer la production', icon: RotateCcw,
    warning: 'Chaque micro-onduleur de la centrale sera redémarré.',
  },
};

export default function ProductionPvControl({ asset, onDone }) {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onduleurs, setOnduleurs] = useState(asset.onduleurs_declares || null);
  const sansOnduleur = onduleurs?.nombre === 0;
  const motif = sansOnduleur
    ? `Aucun onduleur déclaré dans S-Miles pour cette centrale${
      onduleurs?.vu_a ? ` (vérifié le ${horodatageTexte(onduleurs.vu_a)})` : ''}.`
    : null;

  const lancer = async (geste) => {
    setPending(null);
    setBusy(true);
    try {
      const r = await api.post(`/centrales-pv/actifs/${asset.id}/production/${geste}`);
      const { envoyes = 0, refuses = 0 } = r.data || {};
      toast.success(`${GESTES[geste].label} : ${envoyes} ordre${envoyes > 1 ? 's' : ''} envoyé`
        + `${envoyes > 1 ? 's' : ''}${refuses ? `, ${refuses} refusé${refuses > 1 ? 's' : ''}` : ''}.`
        + " L'exécution se confirme au journal, de quelques secondes à cinq minutes.");
      setOnduleurs({ nombre: envoyes + refuses, vu_a: new Date().toISOString() });
      onDone?.();
    } catch (e) {
      if (e.response?.status === 409) {
        setOnduleurs({ nombre: 0, vu_a: new Date().toISOString() });
      }
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : detail?.message || `${GESTES[geste].label} : l'ordre n'est pas parti.`);
    }
    setBusy(false);
  };

  return (
    <div className="w-full flex flex-wrap items-center gap-2 pt-2 border-t border-neutral-100"
         data-testid={`production-pv-${asset.id}`}>
      {Object.entries(GESTES).map(([geste, def]) => {
        const Icon = def.icon;
        return (
          <Button
            key={geste} size="sm" variant="outline" className="h-8 text-[12px]"
            disabled={busy || sansOnduleur}
            title={motif || undefined}
            onClick={() => setPending({
              geste, title: def.label, target: asset.client_nom,
              targetLabel: 'Centrale', warning: def.warning, tone: def.tone,
              confirmLabel: def.label,
            })}
            data-testid={`production-${geste}-${asset.id}`}
          >
            <Icon className="w-3.5 h-3.5 mr-1" /> {def.label}
          </Button>
        );
      })}
      {motif && (
        <span className="text-[11px] text-neutral-500" data-testid={`production-motif-${asset.id}`}>
          {motif}
        </span>
      )}
      <ConfirmDialog
        pending={pending}
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => lancer(pending.geste)}
      />
    </div>
  );
}
